import { useMutation } from '@apollo/client/react';
import { useExperimentsEligibleProps } from '@seek/candidate-feature-flag';
import { useMelwaysInfo, useMelwaysLink } from '@seek/melways-react';
import { metrics } from '@seek/metrics-js';
import { useEffect, useMemo, useRef } from 'react';
import { v4 as uuid } from 'uuid';

import { TRACK_JOB_APPLICATION_STARTED } from 'src/components/App/application-started/track-job-application-started.graphql';
import { Page, useTrackingClient } from 'src/context/tracking';
import { useTestRecord } from 'src/context/use-test-record';
import { useAuth } from 'src/context/useAuth';
import { usePersonalDetails } from 'src/context/usePersonalDetails';
import { useApplicationProcess } from 'src/hooks/useApplicationProcess';
import { useEnhancedHubble } from 'src/hooks/useEnhancedHubble';
import { logError, logInfo } from 'src/modules/error-logger/browserLog';
import { getGraphQLErrors } from 'src/modules/graphql-client/errors';
import { GraphQLErrorCode } from 'src/modules/graphql-client/types';
import { getJobSolMetadata } from 'src/modules/solTracker/solMetadata';
import { isSeekTest } from 'src/modules/testUtils';
import type { Config } from 'src/types';
import { didResolverErrorOccur, getSessionId, navigateToUrl } from 'src/utils';

interface Arguments {
  jobId: string;
  config: Config;
  applicationCorrelationId: string;
  isNewApplication: boolean;
}

export const useInitApp = ({
  jobId,
  config,
  applicationCorrelationId: appId,
  isNewApplication,
}: Arguments) => {
  const {
    tealiumEligibleProps: { experimentsEligible, hubbleExperiments },
  } = useExperimentsEligibleProps();
  const hubble = useEnhancedHubble();
  const { testRecord } = useTestRecord();
  const { language } = useMelwaysInfo();
  const {
    loading: userLoading,
    error: userError,
    isAuth,
    candidateId,
    trackingId,
    profileVisibilityId,
  } = useAuth();
  const { data } = usePersonalDetails();
  const verifiedIdentityName = data?.viewer?.verifiedIdentity?.name;
  const jdvUrl = useMelwaysLink(config.MISSING_JOB + jobId);
  const applicationProcess = useApplicationProcess();
  const jobApplicationProcess = applicationProcess.data;
  const serialApplicantTrackingCorrelationId =
    jobApplicationProcess?.requiredVerifications?.trackingCorrelationId;
  const isLinkOut = jobApplicationProcess?.linkOut ?? false;

  const loading = userLoading || applicationProcess.loading;
  const userErrors = getGraphQLErrors(userError).filter(
    (err) => err.extensions?.code !== GraphQLErrorCode.unauthenticated,
  );
  const applicationProcessErrors = getGraphQLErrors(applicationProcess.error);

  // Errors that should receive special logging
  const loggableErrorPaths = [
    ['jobApplicationProcess', 'questionnaire'],
    ['jobApplicationProcess', 'requiredVerifications'],
  ];

  // Non-blocking errors: these will be logged but won't show the error page
  const nonBlockingErrorPaths = [
    ['jobApplicationProcess', 'requiredVerifications'],
  ];

  // Log errors that match loggable paths
  if (applicationProcessErrors.length > 0) {
    applicationProcessErrors.forEach((err) => {
      const matchingPath = loggableErrorPaths.find((path) =>
        didResolverErrorOccur(path, [err]),
      );
      if (matchingPath) {
        const pathName = matchingPath[matchingPath.length - 1];
        logError(
          {
            ...applicationProcess.error,
            type: `${pathName}-fetch-failed`,
            message: `Failed to resolve ${pathName}`,
          },
          { jobId, applicationCorrelationId: appId, error: err },
        );
      }
    });
  }

  // Check if all application process errors are non-blocking
  const hasOnlyNonBlockingErrors =
    applicationProcessErrors.length > 0 &&
    applicationProcessErrors.every((err) =>
      nonBlockingErrorPaths.some((path) => didResolverErrorOccur(path, [err])),
    );

  // Only set error if there are blocking errors
  const applicationProcessError = hasOnlyNonBlockingErrors
    ? undefined
    : applicationProcess.error;
  const error =
    applicationProcessError || (userErrors.length > 0 ? userError : undefined);
  const [trackApplicationStarted] = useMutation(TRACK_JOB_APPLICATION_STARTED);

  const trackingProps = useMemo(() => {
    const jobDetails = jobApplicationProcess?.job;

    return {
      isLoggedIn: isAuth,
      loginId: candidateId || null,
      profileGuid: trackingId || null,
      advertiserId: jobDetails?.advertiser?.id || '',
      advertiserName: jobDetails?.advertiser?.name || '',
      jobId: jobDetails?.id || '',
      jobLocation: jobApplicationProcess?.location?.name,
      jobLocationId: jobApplicationProcess?.location?.id,
      jobArea: jobApplicationProcess?.area?.name,
      jobAreaId: jobApplicationProcess?.area?.id,
      jobClassification: jobApplicationProcess?.classification?.name,
      jobClassificationId: jobApplicationProcess?.classification?.id,
      jobSubClassification:
        jobApplicationProcess?.classification?.subClassification?.name,
      jobSubClassificationId:
        jobApplicationProcess?.classification?.subClassification?.id,
      jobHasRoleRequirements:
        jobDetails?.tracking?.hasRoleRequirements || false,
      jobHasScreen: false,
      jobIsLinkOut: Boolean(jobApplicationProcess?.linkOut),
      isProfileApply: true,
      jobListingSource: jobDetails?.source,
      isCandidateIdentityVerified: Boolean(verifiedIdentityName),
      brand: config.BRAND,
      siteCountry: config.LOCALE,
      siteLanguage: language,
      zone: config.ZONE,
      experimentsEligible,
      hubbleExperiments,
      applicationCorrelationId: appId,
      profileVisibilityLevel: profileVisibilityId
        ? profileVisibilityId.toLowerCase()
        : 'not set',
    };
  }, [
    jobApplicationProcess,
    isAuth,
    candidateId,
    trackingId,
    config,
    language,
    verifiedIdentityName,
    experimentsEligible,
    hubbleExperiments,
    appId,
    profileVisibilityId,
  ]);

  const trackingClient = useTrackingClient(trackingProps);
  const trackingCorrelationIdLoad = useRef(uuid());
  const trackingCorrelationIdStart = useRef(uuid());

  // Redirect to JDV: Missing job / HK Private Advertiser
  useEffect(() => {
    const apLoading = applicationProcess.loading;
    const job = jobApplicationProcess?.job;
    const isMissingJob = !apLoading && !job;
    const isHKPrivateAdvertiser =
      !apLoading &&
      job?.tracking?.isPrivateAdvertiser &&
      config.LOCALE === 'HK';

    if (isMissingJob) {
      logError(
        {
          type: 'job-not-found',
          message: `Failed to resolve job ${jobId}, job missing`,
        },
        { jobId },
      );
    }

    if (isMissingJob || isHKPrivateAdvertiser) {
      return navigateToUrl(jdvUrl, Boolean(isHKPrivateAdvertiser));
    }
  }, [applicationProcess, config, jobId, jdvUrl, jobApplicationProcess]);

  useEffect(() => {
    if (!loading && !isLinkOut && isAuth && !applicationProcess.loading) {
      trackingClient.trackView({
        view: Page.DOCUMENT_PAGE,
        eventName: ['apply_load_succeeded'],
        trackingCorrelationId: trackingCorrelationIdLoad.current,
      });

      hubble.trackEvent(
        'job_apply_displayed',
        {
          jobId,
          solMetadata: getJobSolMetadata(jobId),
          applicationCorrelationId: appId,
          auxiliaryBackendServices: {
            requiredIdentityVerification: {
              serviceToken: serialApplicantTrackingCorrelationId || '',
            },
          },
        },
        {
          trackingCorrelationId: trackingCorrelationIdLoad.current,
        },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, isAuth, isLinkOut, applicationProcess.loading]);

  useEffect(() => {
    metrics.count('Seek-Test-Hubble', [`seek-test:${isSeekTest()}`]);
  }, [testRecord]);

  useEffect(() => {
    if (loading) {
      return;
    }

    const sendApplicationStartedEvents = async () => {
      metrics.sendInitialLoadTime('job-apply-frontend');
      trackingClient.trackView({
        view: Page.DOCUMENT_PAGE,
        eventName: ['apply_start_succeeded'],
        trackingCorrelationId: trackingCorrelationIdStart.current,
      });

      hubble.trackEvent(
        'job_apply_started',
        {
          jobId,
          thirdPartyLink: false,
          applicationCorrelationId: appId,
        },
        {
          trackingCorrelationId: trackingCorrelationIdStart.current,
        },
      );

      await trackApplicationStarted({
        variables: {
          input: {
            jobId,
            sessionId: getSessionId(),
            applicationCorrelationId: appId,
            isProfileApply: true,
          },
        },
      });

      logInfo(`Application started for JobId ${jobId}`, 'application-started', {
        jobId,
        applicationCorrelationId: appId,
      });
    };

    if (isNewApplication && !isLinkOut) {
      sendApplicationStartedEvents();
    }

    // only run on mount once when loading is complete
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  return {
    loading: !error && loading,
    error: !isLinkOut && error,
    isLinkOut,
  };
};
